import { useMemo, useState } from "react";
import type { SVGProps } from "react";
import { useCustom, useGetLocale, useTranslate } from "@refinedev/core";
import { CalendarIcon, MicrochipIcon, PawIcon } from "@fawkes/ui";

import { LoadingState } from "../../components/states";
import { FosterError } from "./FosterError";
import type { Page } from "../../shell/nav";
import type { Locale } from "../../i18n/messages";
import { AuthImage } from "./AuthImage";
import { formatDateLong, formatDateShort } from "./format";
import type { PortalAnimal, PortalAnimals } from "./types";
import "./mijn-dieren.css";

// fix/foster-login-redesign — Mijn dieren in the foster style (mockup): header + 3 stat cards + a
// search/filter/sort bar + animal cards. Federated over BackofficeClient (portal/animals), authenticated
// per-animal photos via AuthImage. A foster cannot add animals → no "Nieuw dier toevoegen" button.
// Graceful degradation: a `degraded` payload still renders the animals it got, with a banner above.

// Dutch plurals for the dominant-species stat card (the species string is the backoffice's Dutch value).
const SPECIES_PLURAL: Record<string, string> = {
  Hond: "Honden",
  Kat: "Katten",
  Konijn: "Konijnen",
  Cavia: "Cavia's",
  Vogel: "Vogels",
  Hamster: "Hamsters",
  Knaagdier: "Knaagdieren",
  Fret: "Fretten",
};

type SortKey = "newest" | "oldest" | "name";

function SearchIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <circle cx={11} cy={11} r={7} /><path d="m21 21-4.3-4.3" />
    </svg>
  );
}
function OpenIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <path d="M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    </svg>
  );
}

// Species glyphs in the @fawkes/ui line style (the backoffice icon set only has a paw, so the
// dog/cat heads are drawn here) — used on the dominant-species stat card so it isn't a duplicate paw.
function DogIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={24} height={24} fill="none" stroke="currentColor" strokeWidth={1.9}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <path d="M7.5 6C5.7 5.4 4.3 6.6 4.5 8.8c.1 1.4.8 2.5 1.9 3.1" />
      <path d="M16.5 6c1.8-.6 3.2.6 3 2.8-.1 1.4-.8 2.5-1.9 3.1" />
      <path d="M6.3 9.5C6.3 6.7 8.8 4.8 12 4.8s5.7 1.9 5.7 4.7c0 2.2-1.1 4.1-2.8 5.3-.9.6-1.8 1.2-2.9 1.2s-2-.6-2.9-1.2C7.4 13.6 6.3 11.7 6.3 9.5Z" />
      <path d="M9.6 10.6h.01M14.4 10.6h.01" />
      <path d="M12 13v1.1M10.7 14.4c.4.5 1.9.5 2.6 0" />
    </svg>
  );
}
function CatIcon(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" width={24} height={24} fill="none" stroke="currentColor" strokeWidth={1.9}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <path d="M6 7 4.6 4 8.2 6M18 7l1.4-3L15.8 6" />
      <path d="M6 7c-.6 1.6-.6 3.6.3 5.2C7.5 14.4 9.6 16 12 16s4.5-1.6 5.7-3.8c.9-1.6.9-3.6.3-5.2" />
      <path d="M9.7 10.8h.01M14.3 10.8h.01" />
      <path d="M12 12.4v1M10.6 13.9c.5.5 2.3.5 2.8 0" />
      <path d="M12 12.8 9.4 12.1M12 12.8l2.6-.7" />
    </svg>
  );
}
/** The right glyph for a species stat card: dog/cat head, else the generic paw. */
function speciesIcon(species: string) {
  const s = species.toLowerCase();
  if (s.startsWith("hond")) return <DogIcon />;
  if (s.startsWith("kat")) return <CatIcon />;
  return <PawIcon width={22} height={22} />;
}

/** Botanical sprig drawn on the right of each card (decorative, fills the right area like the mockup). */
function CardLeaf(p: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 320 180" fill="currentColor" aria-hidden="true" {...p}>
      <path d="M8 172C96 162 188 120 312 18" fill="none" stroke="currentColor" strokeWidth={4} strokeLinecap="round" />
      <path d="M74 148c-19-3-32-19-33-40 20 3 33 19 33 40z" />
      <path d="M74 148c3-19 19-32 40-33-3 20-19 33-40 33z" />
      <path d="M126 120c-19-3-32-19-33-40 20 3 33 19 33 40z" />
      <path d="M126 120c3-19 19-32 40-33-3 20-19 33-40 33z" />
      <path d="M178 90c-17-3-29-17-30-36 18 3 29 17 30 36z" />
      <path d="M178 90c3-17 17-29 36-30-3 18-17 29-36 30z" />
      <path d="M226 60c-15-3-25-16-26-33 17 3 26 16 26 33z" />
      <path d="M226 60c3-15 16-25 33-26-3 17-16 26-33 26z" />
      <circle cx="266" cy="34" r="6.5" />
      <circle cx="283" cy="48" r="5" />
    </svg>
  );
}

function AnimalRow({ animal, locale, onOpen }: { animal: PortalAnimal; locale: Locale; onOpen: () => void }) {
  const t = useTranslate();
  return (
    <div
      className="fmd-card"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <CardLeaf className="fmd-deco" />
      <AuthImage
        className="fmd-photo"
        path={
          animal.hasPhoto
            ? `/portal/organizations/${animal.organizationUuid}/animals/${animal.dierUuid}/photo`
            : null
        }
        alt={animal.name ? `${t("portal.animals.photoAltNamed")}${animal.name}` : t("portal.animals.photoAlt")}
        size="130px"
      />
      <div className="fmd-info">
        <div className="fmd-name-row">
          <h3 className="fmd-name">{animal.name || "—"}</h3>
          {animal.species ? <span className="fmd-badge">{animal.species}</span> : null}
        </div>
        {animal.breed ? <div className="fmd-breed">{animal.breed}</div> : null}
        <div className="fmd-meta">
          {animal.dateOfBirth ? (
            <span className="fmd-meta-item" title={t("portal.animals.dobLabel")}>
              <CalendarIcon width={15} height={15} />
              {formatDateShort(animal.dateOfBirth, locale)}
            </span>
          ) : null}
          {animal.chipNumber ? (
            <span className="fmd-meta-item" title={t("portal.animals.chipLabel")}>
              <MicrochipIcon width={15} height={15} />
              {animal.chipNumber}
            </span>
          ) : null}
        </div>
        {animal.since ? (
          <span className="fmd-status">
            <span className="fmd-dot" aria-hidden="true" />
            {t("portal.animals.status.since")}
            {formatDateLong(animal.since, locale)}
          </span>
        ) : null}
      </div>
      <div className="fmd-actions">
        <button
          className="fmd-open"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
        >
          <OpenIcon />
          {t("portal.animals.open")}
        </button>
      </div>
    </div>
  );
}

export function MijnDieren({
  onOpenAnimal,
  onNavigate,
}: {
  onOpenAnimal: (organizationUuid: string, dierUuid: string) => void;
  onNavigate?: (page: Page) => void;
}) {
  const t = useTranslate();
  const locale = (useGetLocale()() ?? "nl") as Locale;
  const { data, isLoading, isError, refetch } = useCustom<PortalAnimals>({
    url: "portal/animals",
    method: "get",
  });

  const [search, setSearch] = useState("");
  const [species, setSpecies] = useState("all");
  const [sort, setSort] = useState<SortKey>("newest");

  const animals = useMemo(() => data?.data?.animals ?? [], [data]);

  const speciesOptions = useMemo(
    () => [...new Set(animals.map((a) => a.species).filter((s): s is string => Boolean(s)))].sort(),
    [animals],
  );

  // Dominant species (most common) for the middle stat card.
  const dominant = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of animals) {
      if (a.species) {
        counts.set(a.species, (counts.get(a.species) ?? 0) + 1);
      }
    }
    let best: { species: string; count: number } | null = null;
    for (const [s, c] of counts) {
      if (!best || c > best.count) {
        best = { species: s, count: c };
      }
    }
    return best;
  }, [animals]);

  // "Laatst bijgewerkt" — the most recent placement date across the animals (the mockup's reference).
  const lastUpdated = useMemo(
    () => animals.map((a) => a.since).filter((s): s is string => Boolean(s)).sort().at(-1) ?? null,
    [animals],
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = animals.filter((a) => {
      if (species !== "all" && a.species !== species) {
        return false;
      }
      if (!q) {
        return true;
      }
      return (a.name ?? "").toLowerCase().includes(q) || (a.breed ?? "").toLowerCase().includes(q);
    });
    const sorted = [...list];
    if (sort === "name") {
      sorted.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    } else {
      sorted.sort((a, b) => (b.since ?? "").localeCompare(a.since ?? ""));
      if (sort === "oldest") {
        sorted.reverse();
      }
    }
    return sorted;
  }, [animals, search, species, sort]);

  if (isLoading) {
    return <LoadingState />;
  }
  if (isError || !data?.data) {
    return <FosterError onRetry={() => void refetch()} onNavigate={onNavigate} />;
  }

  const degraded = data.data.degraded;

  return (
    <div className="foster-mijn-dieren">
      <header className="fmd-header">
        <span className="fmd-header-icon">
          <PawIcon width={26} height={26} />
        </span>
        <div className="fmd-heading">
          <h1>{t("portal.animals.title")}</h1>
          <p>{t("portal.animals.subtitle")}</p>
        </div>
      </header>

      <div className="fmd-stats">
        <div className="fmd-stat">
          <span className="fmd-stat-icon fmd-stat-icon--green">
            <PawIcon width={22} height={22} />
          </span>
          <div>
            <div className="fmd-stat-title">{t("portal.animals.stat.total")}</div>
            <div className="fmd-stat-value">{animals.length}</div>
          </div>
        </div>
        <div className="fmd-stat">
          <span className="fmd-stat-icon fmd-stat-icon--neutral">
            {dominant ? speciesIcon(dominant.species) : <PawIcon width={22} height={22} />}
          </span>
          <div>
            <div className="fmd-stat-title">
              {dominant ? (SPECIES_PLURAL[dominant.species] ?? dominant.species) : "—"}
            </div>
            <div className="fmd-stat-value">{dominant ? dominant.count : 0}</div>
          </div>
        </div>
        <div className="fmd-stat">
          <span className="fmd-stat-icon fmd-stat-icon--green">
            <CalendarIcon width={22} height={22} />
          </span>
          <div>
            <div className="fmd-stat-title">{t("portal.animals.stat.lastUpdated")}</div>
            <div className="fmd-stat-value fmd-stat-value--sm">
              {lastUpdated ? formatDateLong(lastUpdated, locale) : "—"}
            </div>
          </div>
        </div>
      </div>

      <div className="fmd-toolbar">
        <div className="fmd-search">
          <SearchIcon />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("portal.animals.search")}
            aria-label={t("portal.animals.search")}
          />
        </div>
        <select
          className="fmd-select"
          value={species}
          onChange={(e) => setSpecies(e.target.value)}
          aria-label={t("portal.animals.filter.allSpecies")}
        >
          <option value="all">{t("portal.animals.filter.allSpecies")}</option>
          {speciesOptions.map((s) => (
            <option key={s} value={s}>
              {SPECIES_PLURAL[s] ?? s}
            </option>
          ))}
        </select>
        <select
          className="fmd-select"
          value={sort}
          onChange={(e) => setSort(e.target.value as SortKey)}
          aria-label={t("portal.animals.sort.newest")}
        >
          <option value="newest">{t("portal.animals.sort.newest")}</option>
          <option value="oldest">{t("portal.animals.sort.oldest")}</option>
          <option value="name">{t("portal.animals.sort.name")}</option>
        </select>
      </div>

      {degraded ? (
        <p className="fmd-banner" role="status">
          {t("portal.backofficeUnavailable")}
        </p>
      ) : null}

      <div className="fmd-list">
        {animals.length === 0 ? (
          <p className="fmd-empty">{t("portal.animals.empty")}</p>
        ) : visible.length === 0 ? (
          <p className="fmd-empty">{t("portal.animals.noResults")}</p>
        ) : (
          visible.map((animal) => (
            <AnimalRow
              key={`${animal.organizationUuid}:${animal.dierUuid}`}
              animal={animal}
              locale={locale}
              onOpen={() => onOpenAnimal(animal.organizationUuid, animal.dierUuid)}
            />
          ))
        )}
      </div>
    </div>
  );
}
